import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Clock, MapPin, CalendarDays, IndianRupee } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import DashboardLayout from "@/components/DashboardLayout";
import { StatusBadge } from "@/components/Shared";
import { api, formatApiError } from "@/lib/api";
import { payProgramFee } from "@/lib/razorpay";
import { useAuth } from "@/context/AuthContext";

function DetailContent() {
    const { id } = useParams();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [it, setIt] = useState(null);
    const [application, setApplication] = useState(null);
    const [applyOpen, setApplyOpen] = useState(false);
    const [why, setWhy] = useState("");
    const [busy, setBusy] = useState(false);
    const [paying, setPaying] = useState(false);

    useEffect(() => {
        api.get(`/internships/${id}`).then((r) => setIt(r.data)).catch(() => toast.error("Internship not found"));
    }, [id]);

    useEffect(() => {
        if (user?.role === "student") {
            api.get("/applications").then((r) => {
                const found = r.data.find((a) => a.internship_id === id);
                if (found) setApplication(found);
            }).catch(() => {});
        }
    }, [user, id]);

    const apply = async () => {
        setBusy(true);
        try {
            const { data } = await api.post("/applications", { internship_id: id, answers: { why } });
            setApplication(data);
            setApplyOpen(false);
            toast.success("Application submitted!");
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setBusy(false);
        }
    };

    const pay = async () => {
        setPaying(true);
        try {
            const result = await payProgramFee(application, user);
            if (result.ok) {
                toast.success("Payment confirmed!");
                setApplication({ ...application, payment_status: "paid" });
            } else if (result.message) {
                toast.info(result.message);
            }
        } catch (e) {
            toast.error(formatApiError(e));
        } finally {
            setPaying(false);
        }
    };

    if (!it) return <p className="text-sm text-muted-foreground">Loading...</p>;

    return (
        <div className="mx-auto max-w-5xl" data-testid="internship-detail">
            <button onClick={() => navigate(-1)} data-testid="back-button" className="mb-6 flex items-center gap-2 text-sm font-medium text-muted-foreground transition-colors duration-200 hover:text-primary">
                <ArrowLeft className="h-4 w-4" /> Back
            </button>
            <div className="grid gap-8 lg:grid-cols-3">
                <div className="lg:col-span-2">
                    <div className="flex flex-wrap items-center gap-3">
                        <span className="rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">{it.domain}</span>
                        <span className="rounded-full bg-secondary/30 px-3 py-1 text-xs font-semibold">{it.difficulty}</span>
                    </div>
                    <h1 className="mt-4 font-display text-3xl font-bold tracking-tight">{it.title}</h1>
                    <p className="mt-2 text-sm text-muted-foreground">Posted by {it.posted_by_name}</p>
                    <p className="mt-6 text-base leading-relaxed text-muted-foreground">{it.description}</p>

                    <h2 className="mt-10 font-display text-lg font-semibold">What's included</h2>
                    <ul className="mt-4 grid gap-2.5 sm:grid-cols-2">
                        {it.includes?.map((inc) => (
                            <li key={inc} className="flex items-center gap-2.5 text-sm"><CheckCircle2 className="h-4 w-4 shrink-0 text-primary" />{inc}</li>
                        ))}
                    </ul>

                    <h2 className="mt-10 font-display text-lg font-semibold">Skills</h2>
                    <div className="mt-4 flex flex-wrap gap-2">
                        {it.skills?.map((s) => <span key={s} className="rounded-full bg-muted px-3 py-1.5 text-xs font-medium">{s}</span>)}
                    </div>

                    <h2 className="mt-10 font-display text-lg font-semibold">Eligibility</h2>
                    <p className="mt-3 text-sm leading-relaxed text-muted-foreground">{it.eligibility}</p>
                </div>

                <div>
                    <div className="card-soft sticky top-24 space-y-5 p-6" data-testid="apply-card">
                        <div>
                            <p className="font-display text-3xl font-bold text-primary">${it.fee_usd}</p>
                            <p className="text-xs text-muted-foreground">Program fee (not a salary or stipend)</p>
                        </div>
                        <div className="space-y-3 border-t border-border/40 pt-5 text-sm">
                            <p className="flex items-center gap-2.5"><Clock className="h-4 w-4 text-muted-foreground" />{it.duration_months} months</p>
                            <p className="flex items-center gap-2.5"><MapPin className="h-4 w-4 text-muted-foreground" />{it.mode} · {it.location}</p>
                            <p className="flex items-center gap-2.5"><CalendarDays className="h-4 w-4 text-muted-foreground" />Apply by {it.deadline}</p>
                        </div>
                        {!user && (
                            <Button data-testid="detail-login-to-apply" className="w-full rounded-full" onClick={() => navigate("/login")}>Log in to apply</Button>
                        )}
                        {user && user.role !== "student" && (
                            <p className="rounded-lg bg-muted p-3 text-xs text-muted-foreground">Only student accounts can apply to internships.</p>
                        )}
                        {user?.role === "student" && !application && (
                            <Button data-testid="apply-now-button" className="w-full rounded-full" onClick={() => setApplyOpen(true)}>Apply Now</Button>
                        )}
                        {application && (
                            <div className="space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Status</span>
                                    <StatusBadge status={application.status} />
                                </div>
                                <div className="flex items-center justify-between">
                                    <span className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Payment</span>
                                    <StatusBadge status={application.payment_status} />
                                </div>
                                {application.payment_status !== "paid" && (
                                    <Button data-testid="pay-fee-button" disabled={paying} className="w-full rounded-full" onClick={pay}>
                                        <IndianRupee className="mr-1 h-4 w-4" />{paying ? "Processing..." : "Pay Program Fee"}
                                    </Button>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            <Dialog open={applyOpen} onOpenChange={setApplyOpen}>
                <DialogContent data-testid="apply-dialog">
                    <DialogHeader><DialogTitle>Apply — {it.title}</DialogTitle></DialogHeader>
                    <div className="space-y-4 pt-2">
                        <p className="text-sm text-muted-foreground">Your TechBloom profile is attached automatically. Add anything extra below.</p>
                        <div className="space-y-2">
                            <Label htmlFor="why">Why are you a good fit? (optional)</Label>
                            <Textarea id="why" data-testid="apply-why-input" rows={4} value={why} onChange={(e) => setWhy(e.target.value)} className="rounded-lg" />
                        </div>
                        <Button data-testid="submit-application-button" disabled={busy} className="w-full rounded-full" onClick={apply}>
                            {busy ? "Submitting..." : "Submit Application"}
                        </Button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}

export default function InternshipDetail() {
    const { user } = useAuth();
    if (user) return <DashboardLayout title="Internship Details"><DetailContent /></DashboardLayout>;
    return (
        <div className="bg-background">
            <Header />
            <div className="px-6 py-14"><DetailContent /></div>
            <Footer />
        </div>
    );
}
